import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import PublicLayout from '@/components/layouts/PublicLayout';
import RootLayout from '@/components/layouts/RootLayout';
import AdminRoute from '@/components/shared/AdminRoute';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import NotFoundPage from '@/components/shared/NotFoundPage';
import ProtectedRoute from '@/components/shared/ProtectedRoute';
import PublicOnlyRoute from '@/components/shared/PublicOnlyRoute';
import { getPublicRoutes, getSharedAdminRoutes, getSharedProtectedRoutes } from './shared';
import { withSuspense } from './utils';

// Lazy-load platform feature pages
const OrganizationsListPage = lazy(
  () => import('@/features/platform/organizations/pages/OrganizationsListPage')
);
const OrganizationDetailPage = lazy(
  () => import('@/features/platform/organizations/pages/OrganizationDetailPage')
);
const TrialDashboardPage = lazy(
  () => import('@/features/platform/trials/pages/TrialDashboardPage')
);
const TrialSetupReviewPage = lazy(
  () => import('@/features/platform/trial-setup/pages/TrialSetupReviewPage')
);
const PublicTrialSetupPage = lazy(
  () => import('@/features/platform/trial-setup/pages/PublicTrialSetupPage')
);
const EventGeneratorPage = lazy(
  () => import('@/features/platform/admin-event-generator/pages/EventGeneratorPage')
);
const NotificationsPage = lazy(() => import('@/features/platform/notifications/NotificationsPage'));
const TrialLiveViewPage = lazy(() => import('@/features/platform/live/pages/TrialLiveViewPage'));
const DeleteDataPage = lazy(() => import('@/features/platform/public/pages/DeleteDataPage'));

export function getPlatformRoutes(): RouteObject[] {
  return [
    {
      element: (
        <ErrorBoundary>
          <RootLayout />
        </ErrorBoundary>
      ),
      errorElement: <NotFoundPage />,
      children: [
        // Public Routes (no auth)
        {
          element: <PublicOnlyRoute />,
          children: [
            {
              element: <PublicLayout />,
              children: getPublicRoutes(),
            },
          ],
        },
        // Public pages (no auth required, outside PublicOnlyRoute)
        {
          element: <PublicLayout />,
          children: [
            {
              path: '/public/trial-setup/:publicToken',
              element: withSuspense(PublicTrialSetupPage),
            },
            {
              path: '/delete-data',
              element: withSuspense(DeleteDataPage),
            },
          ],
        },
        // Protected Routes
        {
          element: <ProtectedRoute />,
          children: [
            // Fullscreen live trial view (no dashboard chrome)
            { path: '/trials/:slug/live', element: withSuspense(TrialLiveViewPage) },
            {
              element: <DashboardLayout />,
              children: [
                // Organizations (home page)
                { path: '/', element: <Navigate to="/organizations" replace /> },
                { path: '/organizations', element: withSuspense(OrganizationsListPage) },
                { path: '/organizations/:slug', element: withSuspense(OrganizationDetailPage) },
                {
                  path: '/organizations/:slug/trial-setup/:submissionSlug/review',
                  element: withSuspense(TrialSetupReviewPage),
                },
                // Trials
                { path: '/trials/:slug', element: withSuspense(TrialDashboardPage) },
                { path: '/trials/:slug/:tab', element: withSuspense(TrialDashboardPage) },
                { path: '/trials/:slug/:tab/:id', element: withSuspense(TrialDashboardPage) },
                {
                  path: '/trials/:slug/:tab/:id/:subView',
                  element: withSuspense(TrialDashboardPage),
                },
                // Notifications
                { path: '/notifications', element: withSuspense(NotificationsPage) },
                // Shared protected routes (profile etc.)
                ...getSharedProtectedRoutes(),
                // Admin Routes
                {
                  element: <AdminRoute />,
                  children: [
                    ...getSharedAdminRoutes(),
                    { path: '/admin/event-generator', element: withSuspense(EventGeneratorPage) },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ];
}
